import React, { useState, useEffect } from "react";
import apiClient from "./api/config";
import { adminFetch } from "./utils/adminApi";
import { FiPlus, FiEdit, FiTrash2, FiToggleLeft, FiToggleRight } from "react-icons/fi";
import { BsPinAngle, BsPinAngleFill } from "react-icons/bs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AdminNotificationScreen.css";

const NOTIF_TYPES = [
  { value: "new_feature", label: "Tính năng mới", icon: "🆕" },
  { value: "update", label: "Cập nhật", icon: "🔄" },
  { value: "maintenance", label: "Bảo trì", icon: "🛠️" },
  { value: "event", label: "Sự kiện", icon: "🎉" },
  { value: "announcement", label: "Thông báo", icon: "📢" },
];

const AdminNotificationScreen = () => {
  const [activeTab, setActiveTab] = useState("in-app"); // "in-app" | "email"

  // Email states
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [userEmails, setUserEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // In-app notification states
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [notifForm, setNotifForm] = useState({
    title: "",
    description: "",
    displayDate: "",
    expiryDate: "",
    type: "announcement",
    isPinned: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (activeTab === "email") {
      fetchUserEmails();
    } else {
      fetchNotifications();
    }
  }, [activeTab]);

  // ========== EMAIL FUNCTIONS ==========
  const fetchUserEmails = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/api/admin/users/emails");
      if (response.data.success) {
        setUserEmails(response.data.data);
      }
    } catch (error) {
      console.error("Lỗi khi lấy danh sách email:", error);
      toast.error("Không thể lấy danh sách email users");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.warning("Vui lòng nhập đầy đủ tiêu đề và nội dung");
      return;
    }
    setIsSending(true);
    try {
      const testEmails = ["lephuoc272@gmail.com", "lephuoc.dev1996@gmail.com"];
      const response = await apiClient.post("/api/admin/send-test-email", {
        subject, message, testEmails,
      });
      if (response.data.success) {
        toast.success(`Thành công! ${response.data.message}`);
      } else {
        toast.error(`Lỗi: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra khi gửi email test");
    } finally {
      setIsSending(false);
    }
  };

  const handleSendNotification = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.warning("Vui lòng nhập đầy đủ tiêu đề và nội dung");
      return;
    }
    const confirm = window.confirm(
      `Bạn có chắc muốn gửi thông báo đến ${userEmails.length} users không?`
    );
    if (!confirm) return;

    setIsSending(true);
    setSendResult(null);
    try {
      const response = await apiClient.post("/api/admin/send-notification", { subject, message });
      if (response.data.success) {
        setSendResult(response.data.data);
        toast.success(`Thành công! ${response.data.message}`);
      } else {
        toast.error(`Lỗi: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra khi gửi thông báo");
    } finally {
      setIsSending(false);
    }
  };

  // ========== IN-APP NOTIFICATION FUNCTIONS ==========
  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const result = await adminFetch("/api/admin/in-app-notifications?limit=100");
      setNotifications(result.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Không thể tải danh sách thông báo");
    } finally {
      setNotifLoading(false);
    }
  };

  const resetForm = () => {
    setNotifForm({ title: "", description: "", displayDate: "", expiryDate: "", type: "announcement", isPinned: false });
    setEditingNotif(null);
    setShowForm(false);
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const toLocalDatetime = (timestamp) => {
    if (!timestamp) return "";
    const d = new Date(timestamp._seconds ? timestamp._seconds * 1000 : timestamp);
    if (isNaN(d.getTime())) return "";
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  const openEditForm = (notif) => {
    setEditingNotif(notif);
    setNotifForm({
      title: notif.title || "",
      description: notif.description || "",
      displayDate: toLocalDatetime(notif.displayDate),
      expiryDate: toLocalDatetime(notif.expiryDate),
      type: notif.type || "announcement",
      isPinned: notif.isPinned === true,
    });
    setShowForm(true);
  };

  const handleSubmitNotif = async (e) => {
    e.preventDefault();
    if (!notifForm.title.trim()) {
      toast.warning("Tiêu đề là bắt buộc");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        title: notifForm.title.trim(),
        description: notifForm.description.trim(),
        displayDate: notifForm.displayDate ? new Date(notifForm.displayDate).toISOString() : null,
        expiryDate: notifForm.expiryDate ? new Date(notifForm.expiryDate).toISOString() : null,
        type: notifForm.type || "announcement",
        isPinned: notifForm.isPinned === true,
      };

      if (editingNotif) {
        await adminFetch(`/api/admin/in-app-notifications/${editingNotif.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        toast.success("Cập nhật thông báo thành công!");
      } else {
        await adminFetch("/api/admin/in-app-notifications", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.success("Tạo thông báo mới thành công!");
      }

      resetForm();
      fetchNotifications();
    } catch (error) {
      console.error("Error saving notification:", error);
      toast.error("Có lỗi khi lưu thông báo");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteNotif = async (id) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa thông báo này?")) return;
    try {
      await adminFetch(`/api/admin/in-app-notifications/${id}`, { method: "DELETE" });
      toast.success("Xóa thông báo thành công!");
      fetchNotifications();
    } catch (error) {
      toast.error("Có lỗi khi xóa thông báo");
    }
  };

  const handleToggleActive = async (notif) => {
    try {
      await adminFetch(`/api/admin/in-app-notifications/${notif.id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !notif.isActive }),
      });
      toast.success(notif.isActive ? "Đã ẩn thông báo" : "Đã hiện thông báo");
      fetchNotifications();
    } catch (error) {
      toast.error("Có lỗi khi cập nhật trạng thái");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const ts = timestamp._seconds ? timestamp._seconds * 1000 : timestamp;
    const date = new Date(ts);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="admin-notification-container">
      <div className="admin-notification-header">
        <h1>📧 Quản Lý Thông Báo</h1>
        <p>Gửi thông báo in-app hoặc email đến tất cả users</p>
      </div>

      {/* Tab Switcher */}
      <div className="admin-notif-tabs">
        <button
          className={`admin-notif-tab ${activeTab === "in-app" ? "active" : ""}`}
          onClick={() => setActiveTab("in-app")}
        >
          🔔 Thông Báo In-App
        </button>
        <button
          className={`admin-notif-tab ${activeTab === "email" ? "active" : ""}`}
          onClick={() => setActiveTab("email")}
        >
          📧 Gửi Email
        </button>
      </div>

      <div className="admin-notification-content">
        {activeTab === "in-app" ? (
          <>
            {/* Header actions */}
            <div className="notif-admin-actions">
              <h3>📋 Danh sách thông báo ({notifications.length})</h3>
              <button onClick={openCreateForm} className="notif-create-btn">
                <FiPlus size={18} /> Tạo thông báo mới
              </button>
            </div>

            {/* Create/Edit Form */}
            {showForm && (
              <div className="notif-form-section">
                <h3>{editingNotif ? "✏️ Sửa Thông Báo" : "➕ Tạo Thông Báo Mới"}</h3>
                <form onSubmit={handleSubmitNotif}>
                  <div className="form-group">
                    <label>Tiêu đề *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={notifForm.title}
                      onChange={(e) => setNotifForm({ ...notifForm, title: e.target.value })}
                      placeholder="Nhập tiêu đề thông báo..."
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Nội dung (hỗ trợ HTML)</label>
                    <textarea
                      className="form-textarea"
                      value={notifForm.description}
                      onChange={(e) => setNotifForm({ ...notifForm, description: e.target.value })}
                      placeholder="<p>Nội dung thông báo...</p>"
                      rows={6}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Loại thông báo</label>
                      <div className="notif-type-selector">
                        {NOTIF_TYPES.map((t) => (
                          <button
                            key={t.value}
                            type="button"
                            className={`notif-type-option ${notifForm.type === t.value ? "active" : ""}`}
                            onClick={() => setNotifForm({ ...notifForm, type: t.value })}
                          >
                            <span className="notif-type-icon">{t.icon}</span>
                            <span>{t.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="notif-pin-toggle">
                      <input
                        type="checkbox"
                        checked={notifForm.isPinned}
                        onChange={(e) => setNotifForm({ ...notifForm, isPinned: e.target.checked })}
                      />
                      <BsPinAngle size={16} />
                      <span>Ghim thông báo (luôn hiển thị ở đầu danh sách)</span>
                    </label>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Ngày hiển thị (tùy chọn)</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={notifForm.displayDate}
                        onChange={(e) => setNotifForm({ ...notifForm, displayDate: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Ngày hết hạn (tùy chọn)</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={notifForm.expiryDate}
                        onChange={(e) => setNotifForm({ ...notifForm, expiryDate: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" disabled={isSubmitting} className="send-btn">
                      {isSubmitting ? "⏳ Đang lưu..." : editingNotif ? "💾 Cập nhật" : "✅ Tạo thông báo"}
                    </button>
                    <button type="button" onClick={resetForm} className="clear-btn">
                      ❌ Hủy
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Notification List */}
            {notifLoading ? (
              <div className="notif-loading">Đang tải...</div>
            ) : notifications.length === 0 ? (
              <div className="notif-empty-admin">
                <p>📭 Chưa có thông báo nào. Nhấn "Tạo thông báo mới" để bắt đầu.</p>
              </div>
            ) : (
              <div className="notif-admin-list">
                {notifications.map((notif) => (
                  <div key={notif.id} className={`notif-admin-item ${!notif.isActive ? "inactive" : ""}`}>
                    <div className="notif-admin-item-content">
                      <div className="notif-admin-item-header">
                        <h4>
                          {notif.isPinned && <BsPinAngleFill size={14} className="notif-pin-icon" />}
                          {notif.title}
                        </h4>
                        <div className="notif-admin-badges">
                          {(() => {
                            const typeInfo = NOTIF_TYPES.find(t => t.value === notif.type) || NOTIF_TYPES[4];
                            return (
                              <span className={`notif-type-badge type-${notif.type || "announcement"}`}>
                                {typeInfo.icon} {typeInfo.label}
                              </span>
                            );
                          })()}
                          <span className={`notif-status-badge ${notif.isActive ? "active" : "hidden-status"}`}>
                            {notif.isActive ? "Đang hiển thị" : "Đã ẩn"}
                          </span>
                        </div>
                      </div>
                      {notif.description && (
                        <p className="notif-admin-item-desc">
                          {notif.description.replace(/<[^>]*>/g, "").substring(0, 120)}
                          {notif.description.length > 120 ? "..." : ""}
                        </p>
                      )}
                      <div className="notif-admin-item-meta">
                        <span>📅 Tạo: {formatDate(notif.createdAt)}</span>
                        {notif.displayDate && <span>👁️ Hiển thị từ: {formatDate(notif.displayDate)}</span>}
                        {notif.expiryDate && <span>⏰ Hết hạn: {formatDate(notif.expiryDate)}</span>}
                      </div>
                    </div>
                    <div className="notif-admin-item-actions">
                      <button onClick={() => handleToggleActive(notif)} title={notif.isActive ? "Ẩn" : "Hiện"}>
                        {notif.isActive ? <FiToggleRight size={22} className="text-green-500" /> : <FiToggleLeft size={22} className="text-gray-400" />}
                      </button>
                      <button onClick={() => openEditForm(notif)} title="Sửa">
                        <FiEdit size={18} className="text-blue-500" />
                      </button>
                      <button onClick={() => handleDeleteNotif(notif.id)} title="Xóa">
                        <FiTrash2 size={18} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* EMAIL TAB - Original content */}
            <div className="users-info-section">
              <h3>👥 Thông tin Users</h3>
              {isLoading ? (
                <p>Đang tải danh sách users...</p>
              ) : (
                <div className="users-stats">
                  <div className="stat-item">
                    <span className="stat-label">Tổng số users:</span>
                    <span className="stat-value">{userEmails.length}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Users có email:</span>
                    <span className="stat-value">
                      {userEmails.filter((u) => u.email).length}
                    </span>
                  </div>
                </div>
              )}
              <button onClick={fetchUserEmails} className="refresh-btn" disabled={isLoading}>
                🔄 Làm mới
              </button>
            </div>

            <div className="notification-form-section">
              <h3>✍️ Soạn Thông Báo Email</h3>
              <div className="form-group">
                <label htmlFor="subject">Tiêu đề thông báo:</label>
                <input
                  type="text" id="subject" value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Nhập tiêu đề thông báo..."
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Nội dung thông báo:</label>
                <textarea
                  id="message" value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Nhập nội dung thông báo..."
                  className="form-textarea" rows={8}
                />
              </div>
              <div className="form-actions">
                <button onClick={handleSendTestEmail}
                  disabled={isSending || !subject.trim() || !message.trim()}
                  className="test-btn"
                >
                  {isSending ? "⏳ Đang gửi..." : "🧪 Gửi Test (Admin)"}
                </button>
                <button onClick={handleSendNotification}
                  disabled={isSending || !subject.trim() || !message.trim() || userEmails.length === 0}
                  className="send-btn"
                >
                  {isSending ? "⏳ Đang gửi..." : `📤 Gửi đến ${userEmails.length} Users`}
                </button>
                <button onClick={() => { setSubject(""); setMessage(""); setSendResult(null); }}
                  disabled={isSending} className="clear-btn"
                >
                  🗑️ Xóa Form
                </button>
              </div>
            </div>

            {sendResult && (
              <div className="send-result-section">
                <h3>📊 Kết Quả Gửi Thông Báo</h3>
                <div className="result-stats">
                  <div className="result-item success">
                    <span>✅ Gửi thành công:</span>
                    <span>{sendResult.sent}</span>
                  </div>
                  <div className="result-item failed">
                    <span>❌ Gửi thất bại:</span>
                    <span>{sendResult.failed}</span>
                  </div>
                </div>
                {sendResult.details && sendResult.details.length > 0 && (
                  <div className="result-details">
                    <h4>Chi tiết:</h4>
                    <div className="details-list">
                      {sendResult.details.map((detail, index) => (
                        <div key={index} className={`detail-item ${detail.status}`}>
                          <span className="email">{detail.email}</span>
                          <span className={`status ${detail.status}`}>
                            {detail.status === "success" ? "✅" : "❌"}
                          </span>
                          {detail.error && <span className="error-msg">{detail.error}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AdminNotificationScreen;
